/**
 * Document Repository — static (dummy) document data.
 *
 * PPT "Document Repository" (slides 24-35) is the design authority for this
 * feature: the five repositories, their per-category colour, the document rows
 * and the hover "Extracted Fields" preview all reproduce that deck. The document
 * TABLE is dummy on purpose (per the current scope — only the FILTERS and the
 * save-filters flow are wired to real APIs); every row is shaped like a future
 * `/fetchDocuments` response so a real endpoint can be dropped in later without
 * touching the components.
 *
 * All dummy rows are anchored to campaign **CMPO04BRS725** (the "Clinical
 * Supply - BST" project used throughout the deck), which is a real campaign in
 * the projects tree returned by fetchProjectsAPI. That anchor (plus each row's
 * `stage` / `batchNumber`) is what lets the real Project / Campaign / Stage /
 * Batch filters actually narrow this dummy list — see filterDocuments() in
 * index.jsx.
 *
 * Row shape:
 *   {
 *     id, name, docType,
 *     docId,          // Document ID column — plain text; some docs show '—'
 *     mosymphonyId,   // Mosymphony ID column — unique id generated via Mosymphony;
 *                     // the ONLY hover target (opens the Extracted Fields preview)
 *     fileType,
 *     level: 'Campaign' | 'Stage' | 'Batch',   // Level column
 *     levelId,                                  // Level ID column (campaign id / stage / batch no.)
 *     version, lastUpdated,
 *     // scope anchors (used only for filtering, never displayed as-is):
 *     cmo, site, product, campaignId, stage, batchNumber,
 *     // hover "Extracted Fields" preview (label / value pairs, FSD field map):
 *     extractedFields: [{ label, value }]
 *   }
 *
 * A value that was not captured is shown as an em dash ('—'), exactly as the
 * FSD's Extracted Field Preview requires.
 */

// Common scope for every dummy row (the deck's project/campaign/CMO/site).
const CMO_NAME = 'Sai Life Sciences Limited';
const SITE = 'Unit-IV';
const PRODUCT = 'BST';
const CAMPAIGN = 'CMPO04BRS725';
const DASH = '—';

// Per-category folder-icon / switch-dot colour (from the PPT).
const ACCENT = {
  techTransfer: '#6D28D9', // violet
  manufacturing: '#2563EB', // blue
  quality: '#DB2777', // magenta
  analytical: '#059669', // green
  safety: '#EA580C', // orange
};

export const REPOSITORIES = [
  {
    key: 'techTransfer',
    name: 'Tech Transfer Documents',
    accent: ACCENT.techTransfer,
    banner: '',
    documents: [
      {
        id: 'tt-1',
        mosymphonyId: 'MSY-0000001',
        name: 'Process_Tech_Pack_Stage A',
        docType: 'Process Tech Pack',
        docId: 'TP-BSQA-001',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage A',
        version: 'v1.0',
        lastUpdated: '18 Mar 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage A',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: 'Unit-II' },
          { label: 'Document ID in document', value: 'TP-BSQA-001' },
          { label: 'Document Type', value: 'Process Tech Pack' },
          { label: 'CMO Product Code', value: 'BSQ' },
          { label: 'Stage', value: 'Stage-A' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Effective Date', value: '18 Mar 2026' },
          { label: 'Next Review Date', value: DASH },
        ],
      },
      {
        id: 'tt-2',
        mosymphonyId: 'MSY-0000002',
        name: 'Analytical_Tech_Pack_Stage E',
        docType: 'Analytical Tech Pack',
        docId: 'SPEC-BSQE-004',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage E',
        version: 'v2.0',
        lastUpdated: '16 Mar 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: 'Unit-II' },
          { label: 'Document ID in document', value: 'SPEC-BSQE-004' },
          { label: 'Document Type', value: 'Analytical Tech Pack' },
          { label: 'CMO Product Code', value: 'BSQ' },
          { label: 'Stage', value: 'Stage-E' },
          { label: 'Version Number', value: '2.0' },
          { label: 'Effective Date', value: '16 Mar 2026' },
          { label: 'Next Review Date', value: DASH },
        ],
      },
    ],
  },

  {
    key: 'manufacturing',
    name: 'Manufacturing Documents',
    accent: ACCENT.manufacturing,
    banner: '',
    documents: [
      {
        id: 'mfg-1',
        mosymphonyId: 'MSY-0000003',
        name: 'Executed_BPR_DBSTE26001',
        docType: 'Executed BPR',
        docId: 'EBR-13671-26-005',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTE26001',
        version: 'v0.8',
        lastUpdated: '20 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: 'DBSTE26001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'EBR-13671-26-005' },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage E' },
          { label: 'Document Type', value: 'Executed BPR' },
          { label: 'Version Number', value: '0.8' },
          { label: 'Batch Number', value: 'DBSTE26001' },
          { label: 'Batch Start Date', value: '02 Jun 2026' },
          { label: 'Batch End Date', value: '18 Jun 2026' },
        ],
      },
      {
        id: 'mfg-2',
        mosymphonyId: 'MSY-0000004',
        name: 'Equipment_Request_Form_DBSTE26001',
        docType: 'Equipment Request Form',
        docId: 'ERF-13671-26-003',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTE26001',
        version: 'v1.0',
        lastUpdated: '18 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: 'DBSTE26001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'ERF-13671-26-003' },
          { label: 'Document Type', value: 'Equipment Request Form' },
          { label: 'Stage', value: 'Stage E' },
          { label: 'Batch Number', value: 'DBSTE26001' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Effective Date', value: '18 Jun 2026' },
        ],
      },
      {
        id: 'mfg-3',
        mosymphonyId: 'MSY-0000005',
        name: 'Executed_BPR_DBSTD25001',
        docType: 'Executed BPR',
        docId: 'EBR-13671-26-004',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTD25001',
        version: 'v0.9',
        lastUpdated: '14 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage D',
        batchNumber: 'DBSTD25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'EBR-13671-26-004' },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage D' },
          { label: 'Document Type', value: 'Executed BPR' },
          { label: 'Version Number', value: '0.9' },
          { label: 'Batch Number', value: 'DBSTD25001' },
          { label: 'Batch Start Date', value: '30 May 2026' },
          { label: 'Batch End Date', value: '12 Jun 2026' },
        ],
      },
      {
        id: 'mfg-4',
        mosymphonyId: 'MSY-0000006',
        name: 'Draft_BPR_CMPO04BRS725',
        docType: 'Draft BPR',
        docId: 'BPR-13671-26-001',
        fileType: 'PDF',
        level: 'Campaign',
        levelId: CAMPAIGN,
        version: 'v0.6',
        lastUpdated: '09 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: null,
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'BPR-13671-26-001' },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: DASH },
          { label: 'Document Type', value: 'Draft BPR' },
          { label: 'Version Number', value: '0.6' },
          { label: 'Batch Number', value: DASH },
        ],
      },
      {
        id: 'mfg-master-bpr',
        mosymphonyId: 'MSY-0000007',
        name: 'Master_BPR_MBPR_Stage A',
        docType: 'Master BPR (MBPR)',
        docId: 'MBPR-BSQA-001',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage A',
        version: 'v0.0',
        lastUpdated: '05 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage A',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'MBPR-BSQA-001' },
          { label: 'Document Type', value: 'Master BPR (MBPR)' },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage A' },
          { label: 'Version Number', value: '0.0' },
          { label: 'Effective Date', value: '05 Jun 2026' },
        ],
      },
      {
        id: 'mfg-5',
        mosymphonyId: 'MSY-0000008',
        name: 'Executed_BPR_DBSTC25001',
        docType: 'Executed BPR',
        docId: 'EBR-13671-26-003',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTC25001',
        version: 'v1.0',
        lastUpdated: '30 Mar 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage C',
        batchNumber: 'DBSTC25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'EBR-13671-26-003' },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage C' },
          { label: 'Document Type', value: 'Executed BPR' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Batch Number', value: 'DBSTC25001' },
          { label: 'Batch Start Date', value: '16 Mar 2026' },
          { label: 'Batch End Date', value: '28 Mar 2026' },
        ],
      },
      {
        id: 'mfg-6',
        mosymphonyId: 'MSY-0000009',
        name: 'Equipment_Request_Form_DBSTC25001',
        docType: 'Equipment Request Form',
        docId: 'ERF-13671-26-002',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTC25001',
        version: 'v1.0',
        lastUpdated: '28 Mar 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage C',
        batchNumber: 'DBSTC25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'ERF-13671-26-002' },
          { label: 'Document Type', value: 'Equipment Request Form' },
          { label: 'Stage', value: 'Stage C' },
          { label: 'Batch Number', value: 'DBSTC25001' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Effective Date', value: '28 Mar 2026' },
        ],
      },
      {
        id: 'mfg-7',
        mosymphonyId: 'MSY-0000010',
        name: 'Executed_BPR_DBSUB25001',
        docType: 'Executed BPR',
        docId: 'EBR-13671-26-002',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSUB25001',
        version: 'v1.0',
        lastUpdated: '28 Feb 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage B',
        batchNumber: 'DBSUB25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'EBR-13671-26-002' },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage B' },
          { label: 'Document Type', value: 'Executed BPR' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Batch Number', value: 'DBSUB25001' },
          { label: 'Batch Start Date', value: '14 Feb 2026' },
          { label: 'Batch End Date', value: '26 Feb 2026' },
        ],
      },
    ],
  },

  {
    key: 'quality',
    name: 'Quality Documents',
    accent: ACCENT.quality,
    banner:
      'Project-wide rollup — every deviation, investigation and change control across all batches and stages, in one place. No need to open each step.',
    documents: [
      {
        id: 'q-1',
        mosymphonyId: 'MSY-0000011',
        name: 'Certificate_of_Analysis_CoA_DBSTE26001',
        docType: 'Certificate of Analysis (CoA)',
        docId: 'COA-13671-26-003',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTE26001',
        version: 'v0.9',
        lastUpdated: '21 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: 'DBSTE26001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Certificate of Analysis (CoA)' },
          { label: 'Document ID in document', value: 'COA-13671-26-003' },
          { label: 'Stage', value: 'Stage E' },
          { label: 'Batch Number', value: 'DBSTE26001' },
          { label: 'Version Number', value: '0.9' },
          { label: 'Effective Date', value: '21 Jun 2026' },
        ],
      },
      {
        id: 'q-2',
        mosymphonyId: 'MSY-0000012',
        name: 'Deviation_Assessment_Report_DBSTD25001',
        docType: 'Deviation Assessment Report',
        docId: 'DAR-13671-26-002',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTD25001',
        version: 'v0.5',
        lastUpdated: '20 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage D',
        batchNumber: 'DBSTD25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document ID in document', value: 'DAR-13671-26-002' },
          { label: 'Reference Deviation Number', value: 'DR-D-BST-25-002' },
          { label: 'Investigation Report Number', value: 'IR-D-BST-25-002' },
          { label: 'Document Type', value: 'Investigation Report' },
          { label: 'Deviation Number', value: 'DR-D-BST-25-002' },
          { label: 'Version Number', value: '0.5' },
          { label: 'Effective Date', value: '20 Jun 2026' },
          { label: 'Next Review Date', value: DASH },
          { label: 'Stage', value: 'Stage D' },
          { label: 'Batch Number', value: 'DBSTD25001' },
        ],
      },
      {
        id: 'q-3',
        mosymphonyId: 'MSY-0000013',
        name: 'Change_Control_Report_DBSTE26001',
        docType: 'Change Control Report',
        docId: 'CC-13671-26-002',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTE26001',
        version: 'v0.4',
        lastUpdated: '19 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: 'DBSTE26001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Process/ Equipment/ Site/ Spec Change Request' },
          { label: 'Change Control Number', value: 'CC-E-BST-26-002' },
          { label: 'Stage', value: 'Stage E' },
          { label: 'Batch Number', value: 'DBSTE26001' },
          { label: 'Effective Date', value: '19 Jun 2026' },
          { label: 'CMO Change Control Status', value: 'Closed' },
        ],
      },
      {
        id: 'q-4',
        mosymphonyId: 'MSY-0000014',
        name: 'Initial_Deviation_Report_DBSTD25001',
        docType: 'Initial Deviation Report',
        docId: 'DEV-13671-26-003',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTD25001',
        version: 'v0.2',
        lastUpdated: '17 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage D',
        batchNumber: 'DBSTD25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'Document Type', value: 'Deviation Report' },
          { label: 'Deviation Number', value: 'DR-D-BST-25-003' },
          { label: 'DR Category', value: 'Minor' },
          { label: 'Stage', value: 'Stage D' },
          { label: 'Batch Number', value: 'DBSTD25001' },
          { label: 'Reporting Date', value: '17 Jun 2026' },
          { label: 'Deviation Status', value: 'Open' },
          { label: 'Informed Sponsor', value: 'Yes' },
        ],
      },
      {
        id: 'q-5',
        mosymphonyId: 'MSY-0000015',
        name: 'Certificate_of_Analysis_CoA_DBSTC25001',
        docType: 'Certificate of Analysis (CoA)',
        docId: 'COA-13671-26-002',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTC25001',
        version: 'v1.0',
        lastUpdated: '03 Apr 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage C',
        batchNumber: 'DBSTC25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Certificate of Analysis (CoA)' },
          { label: 'Document ID in document', value: 'COA-13671-26-002' },
          { label: 'Stage', value: 'Stage C' },
          { label: 'Batch Number', value: 'DBSTC25001' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Effective Date', value: '03 Apr 2026' },
        ],
      },
      {
        id: 'q-6',
        mosymphonyId: 'MSY-0000016',
        name: 'Change_Control_Report_DBSTC25001',
        docType: 'Change Control Report',
        docId: 'CC-13671-26-001',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTC25001',
        version: 'v2.0',
        lastUpdated: '02 Apr 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage C',
        batchNumber: 'DBSTC25001',
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Process/ Equipment/ Site/ Spec Change Request' },
          { label: 'Change Control Number', value: 'CC-C-BST-26-001' },
          { label: 'Stage', value: 'Stage C' },
          { label: 'Batch Number', value: 'DBSTC25001' },
          { label: 'Effective Date', value: '02 Apr 2026' },
          { label: 'CMO Change Control Status', value: 'Closed' },
        ],
      },
    ],
  },

  {
    key: 'analytical',
    name: 'Analytical Documents',
    accent: ACCENT.analytical,
    banner: '',
    documents: [
      {
        id: 'an-1',
        mosymphonyId: 'MSY-0000017',
        name: 'Analytical_Report_DBSTE26001',
        docType: 'Analytical Report',
        docId: 'AR-13671-26-003',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTE26001',
        version: 'v0.8',
        lastUpdated: '20 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: 'DBSTE26001',
        extractedFields: [
          { label: 'Document Type', value: 'LIMS General Report' },
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage E' },
          { label: 'Analytical Report Number', value: 'AR-13671-26-003' },
          { label: 'Batch Number', value: 'DBSTE26001' },
        ],
      },
      {
        id: 'an-2',
        mosymphonyId: 'MSY-0000018',
        name: 'Analytical_Report_DBSTC25001',
        docType: 'Analytical Report',
        docId: 'AR-13671-26-002',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTC25001',
        version: 'v1.0',
        lastUpdated: '01 Apr 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage C',
        batchNumber: 'DBSTC25001',
        extractedFields: [
          { label: 'Document Type', value: 'LIMS General Report' },
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage C' },
          { label: 'Analytical Report Number', value: 'AR-13671-26-002' },
          { label: 'Batch Number', value: 'DBSTC25001' },
        ],
      },
      {
        id: 'an-3',
        mosymphonyId: 'MSY-0000019',
        name: 'Analytical_Report_DBSTA25001',
        docType: 'Analytical Report',
        docId: 'AR-13671-26-001',
        fileType: 'PDF',
        level: 'Batch',
        levelId: 'DBSTA25001',
        version: 'v1.0',
        lastUpdated: '26 Feb 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage A',
        batchNumber: 'DBSTA25001',
        extractedFields: [
          { label: 'Document Type', value: 'LIMS General Report' },
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'CMO Product Code', value: PRODUCT },
          { label: 'Stage', value: 'Stage A' },
          { label: 'Analytical Report Number', value: 'AR-13671-26-001' },
          { label: 'Batch Number', value: 'DBSTA25001' },
        ],
      },
    ],
  },

  {
    key: 'safety',
    name: 'Safety Information',
    accent: ACCENT.safety,
    banner:
      'Safety data is captured at the manufacturing stage level — independent of batch or lot.',
    documents: [
      {
        id: 'sf-1',
        mosymphonyId: 'MSY-0000020',
        name: 'Stage_Safety_Information_Stage E',
        docType: 'Stage Safety Information',
        docId: 'PSI-13671-26-005',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage E',
        version: 'v0.4',
        lastUpdated: '18 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage E',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Stage Safety Information' },
          { label: 'Stage', value: 'Stage E' },
          { label: 'Version Number', value: '0.4' },
          { label: 'Effective Date', value: '18 Jun 2026' },
        ],
      },
      {
        id: 'sf-2',
        mosymphonyId: 'MSY-0000021',
        name: 'Stage_Safety_Information_Stage C',
        docType: 'Stage Safety Information',
        docId: 'PSI-13671-26-003',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage C',
        version: 'v1.1',
        lastUpdated: '15 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage C',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Stage Safety Information' },
          { label: 'Stage', value: 'Stage C' },
          { label: 'Version Number', value: '1.1' },
          { label: 'Effective Date', value: '15 Jun 2026' },
        ],
      },
      {
        id: 'sf-3',
        mosymphonyId: 'MSY-0000022',
        name: 'Stage_Safety_Information_Stage D',
        docType: 'Stage Safety Information',
        docId: 'PSI-13671-26-004',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage D',
        version: 'v1.0',
        lastUpdated: '15 Jun 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage D',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Stage Safety Information' },
          { label: 'Stage', value: 'Stage D' },
          { label: 'Version Number', value: '1.0' },
          { label: 'Effective Date', value: '15 Jun 2026' },
        ],
      },
      {
        id: 'sf-4',
        mosymphonyId: 'MSY-0000023',
        name: 'Stage_Safety_Information_Stage A',
        docType: 'Stage Safety Information',
        docId: 'PSI-13671-26-001',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage A',
        version: 'v2.0',
        lastUpdated: '10 Jan 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage A',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Stage Safety Information' },
          { label: 'Stage', value: 'Stage A' },
          { label: 'Version Number', value: '2.0' },
          { label: 'Effective Date', value: '10 Jan 2026' },
        ],
      },
      {
        id: 'sf-5',
        mosymphonyId: 'MSY-0000024',
        name: 'Stage_Safety_Information_Stage B',
        docType: 'Stage Safety Information',
        docId: 'PSI-13671-26-002',
        fileType: 'PDF',
        level: 'Stage',
        levelId: 'Stage B',
        version: 'v2.0',
        lastUpdated: '10 Jan 2026',
        cmo: CMO_NAME,
        site: SITE,
        product: PRODUCT,
        campaignId: CAMPAIGN,
        stage: 'Stage B',
        batchNumber: null,
        extractedFields: [
          { label: 'CMO Name', value: CMO_NAME },
          { label: 'CMO-Site', value: SITE },
          { label: 'Document Type', value: 'Stage Safety Information' },
          { label: 'Stage', value: 'Stage B' },
          { label: 'Version Number', value: '2.0' },
          { label: 'Effective Date', value: '10 Jan 2026' },
        ],
      },
    ],
  },
];

// Convenience lookup by key.
export const getRepository = (key) =>
  REPOSITORIES.find((r) => r.key === key) || REPOSITORIES[0];

// Map the dropdown labels used in ActionButtons to repository keys.
export const LABEL_TO_KEY = {
  'Tech Transfer': 'techTransfer',
  Manufacturing: 'manufacturing',
  Quality: 'quality',
  Analytical: 'analytical',
  Safety: 'safety',
};
